import {
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  ElementRef,
  Inject,
  Input,
  OnInit,
  Optional,
  ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { redirectToUrl } from '../../common/redirect-to-url';
import {
  ChildTranslationService,
  CultureInfo,
  CultureInfoService,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  TOKENS as ANALYTICS_TOKENS,
  TrackingService,
} from '@trustedshops/tswp-core-analytics';
import { UPGRADE_BUTTON_SLOT } from '../upgrade-journey-content';
import { ProductFeatureUpgradeTranslationService } from '../../services/product-feature-upgrade-translation.service';
import {
  PRODUCT_FEATURE_UPGRADE_BUTTON_CONFIG,
  ProductFeatureUpgradeButtonConfig,
} from './product-feature-upgrade-button.config';

/**
 * Default translation key for the button label, used when the resolved
 * `upgrade-button` slot content provides none.
 */
const DEFAULT_BUTTON_LABEL_TRANSLATION_KEY =
  'upgrade-journey.global.compare-plans.button';

/**
 * Query parameter carrying the UI language on the compare-plans redirect URL.
 */
const LANGUAGE_QUERY_PARAM = 'dl';

/**
 * ISO 639-1 language codes the compare-plans target currently supports. The
 * resolved {@link CultureInfo.iso2LanguageCode} is matched against this set;
 * anything outside it falls back to {@link DEFAULT_LANGUAGE}.
 */
const SUPPORTED_LANGUAGES = ['en', 'fr', 'nl'] as const;

/**
 * Language used for the {@link LANGUAGE_QUERY_PARAM} default when the active
 * culture is not part of {@link SUPPORTED_LANGUAGES}.
 */
const DEFAULT_LANGUAGE = 'en';

/**
 * `detail` payload of the cancelable `comparePlans` {@link CustomEvent}
 * dispatched by {@link ProductFeatureUpgradeButtonComponent}. Carries the URL
 * resolved from the carrier configuration so consumers that override the default
 * behaviour (via `preventDefault()`) can use it as the redirect target.
 */
export interface ComparePlansEvent {
  comparePlansUrl?: string;
}

/**
 * Reusable "compare plans" CTA button used to drive users towards a richer
 * plan tier. Rendered standalone or composed inside the upgrade banner and
 * upgrade page.
 *
 * On click the button dispatches a cancelable `comparePlans` event and, unless
 * a consumer calls `preventDefault()`, navigates to the compare-plans page
 * itself. The target URL is supplied by the hosting carrier through
 * {@link PRODUCT_FEATURE_UPGRADE_BUTTON_CONFIG} (populated from the carrier's
 * environment files). The URL may be relative (in-app router navigation) or
 * absolute (full-page navigation to an external origin).
 *
 * @see {@link https://docs.trustedshops.dev/docs/the-platform-1x1-product-feature-upgrade-button | Documentation}
 */
@Component({
  selector: 'tswp-product-feature-upgrade-button',
  templateUrl: './product-feature-upgrade-button.component.html',
  styleUrls: ['./product-feature-upgrade-button.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: false,
})
export class ProductFeatureUpgradeButtonComponent implements OnInit {
  public static readonly selector = 'tswp-product-feature-upgrade-button';

  private static readonly TYPE = 'ProductFeatureUpgradeButtonComponent';

  /**
   * Resolved translation key for the button label, bound live in the template
   * via `getTranslationForKey(...) | subscribe`. Resolved by the
   * {@link ProductFeatureUpgradeTranslationService} from the `upgrade-button`
   * slot content, falling back to {@link DEFAULT_BUTTON_LABEL_TRANSLATION_KEY}.
   */
  public labelKey: string | null = null;

  /**
   * Required key of the gated product feature the button promotes. Forwarded
   * as the tracking label so click events can be attributed to a specific
   * feature.
   */
  @Input({ required: true }) public productFeatureKey!: string;

  /**
   * Optional extra query parameters appended to the resolved compare-plans URL
   * (merged into any params already present on it, as well as the defaults
   * applied by the button such as {@link LANGUAGE_QUERY_PARAM}). Lets consumers
   * enrich the redirect target with context, e.g. the originating feature or
   * campaign. When a key here duplicates a default (or a param already on the
   * resolved URL), the value supplied here wins.
   *
   * A URL-encoded query string, with or without a leading `?` (e.g.
   * `"?feature=reviews&campaign=q3"`). The caller is responsible for encoding
   * values that contain reserved characters. Accepted as a plain attribute, so
   * it works across frameworks (React, Angular, vanilla, ...).
   */
  @Input() public redirectUrlParams?: string;

  /**
   * Name of the cancelable DOM event dispatched on click. Consumers in any
   * framework listen via `addEventListener('comparePlans', ...)` (or the
   * equivalent template binding) and call `event.preventDefault()` to take
   * over navigation; otherwise the button performs its own redirect.
   */
  private static readonly COMPARE_PLANS_EVENT = 'comparePlans';

  public constructor(
    @Inject(COMMON_TOKENS.TranslationService)
    public readonly translationService: ChildTranslationService,
    private readonly upgradeTranslationService: ProductFeatureUpgradeTranslationService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(ANALYTICS_TOKENS.TrackingService)
    private readonly trackingService: TrackingService,
    @Inject(COMMON_TOKENS.CultureInfoService)
    private readonly cultureInfoService: CultureInfoService,
    private readonly router: Router,
    @Inject(DOCUMENT)
    private readonly document: Document,
    private readonly elementRef: ElementRef<HTMLElement>,
    @Optional()
    @Inject(PRODUCT_FEATURE_UPGRADE_BUTTON_CONFIG)
    private readonly config?: ProductFeatureUpgradeButtonConfig
  ) {}

  public async ngOnInit(): Promise<void> {
    // Resolve the label key via the shared service from the `upgrade-button`
    // slot content (selected by Kameleoon variation / consent), falling back to
    // the default label key. The template binds it live via the subscribe pipe.
    const keys = await this.upgradeTranslationService.resolveSlotKeys(
      ProductFeatureUpgradeButtonComponent.TYPE,
      this.productFeatureKey,
      UPGRADE_BUTTON_SLOT,
      [{ field: 'label', defaultKey: DEFAULT_BUTTON_LABEL_TRANSLATION_KEY }]
    );
    this.labelKey = keys['label'];

    // The key is resolved after an async Kameleoon read, so nudge change
    // detection to bind the live translation for this Shadow-DOM component.
    this.changeDetectorRef.markForCheck();
  }

  public async onComparePlansClicked(): Promise<void> {
    this.trackComparePlansClick();

    const url = await this.appendRedirectParams(this.config?.comparePlansUrl);

    // Dispatch a cancelable, bubbling, composed event so it crosses the
    // custom-element / Shadow DOM boundaries and reaches consumers regardless
    // of framework (React, Angular, vanilla, ...). `dispatchEvent` returns
    // false when a listener called `preventDefault()`, which is how a consumer
    // signals it has taken over navigation.
    const event = new CustomEvent<ComparePlansEvent>(
      ProductFeatureUpgradeButtonComponent.COMPARE_PLANS_EVENT,
      {
        detail: { comparePlansUrl: url },
        bubbles: true,
        composed: true,
        cancelable: true,
      }
    );
    const notOverridden = this.elementRef.nativeElement.dispatchEvent(event);

    if (!notOverridden) {
      this.logService.debug(
        ProductFeatureUpgradeButtonComponent.TYPE,
        `comparePlans overridden by a consumer (preventDefault); skipping redirect. Resolved URL was "${url}".`
      );
      return;
    }

    if (!url) {
      const message =
        'No compare-plans URL configured (PRODUCT_FEATURE_UPGRADE_BUTTON_CONFIG.comparePlansUrl), skipping redirect';
      this.logService.error(
        ProductFeatureUpgradeButtonComponent.TYPE,
        new Error(message)
      );
      return;
    }

    await redirectToUrl(url, this.router, this.document);
  }

  private trackComparePlansClick(): void {
    this.trackingService.trackEvent({
      eventType: 'event',
      eventParameters: [
        'UPGRADE_JOURNEY_COMPARE_PLANS_CLICK',
        {
          productFeatureKey: this.productFeatureKey,
        },
      ],
    });
  }

  private async appendRedirectParams(
    url: string | undefined
  ): Promise<string | undefined> {
    if (!url) {
      return url;
    }

    // Default params (e.g. the UI language) are the base layer. Params already
    // present on the resolved URL and the caller-supplied `redirectUrlParams`
    // are layered on top, so both override a default when the same key repeats.
    // `URLSearchParams` parses a query string and strips a leading `?`, so both
    // "?a=1&b=2" and "a=1&b=2" are accepted for `redirectUrlParams`.
    const [path, existingParams] = url.split('?');
    const merged = await this.resolveDefaultParams();

    // `set` (rather than spreading into the constructor) ensures a later layer
    // replaces an earlier value for the same key instead of appending a second
    // occurrence, giving `redirectUrlParams` the final say on duplicates.
    for (const layer of [
      new URLSearchParams(existingParams),
      new URLSearchParams(this.redirectUrlParams ?? ''),
    ]) {
      layer.forEach((value, key) => merged.set(key, value));
    }

    return `${path}?${merged}`;
  }

  /**
   * Default query parameters applied to every compare-plans redirect. Currently
   * just the UI {@link LANGUAGE_QUERY_PARAM}, resolved from the active culture.
   */
  private async resolveDefaultParams(): Promise<URLSearchParams> {
    const params = new URLSearchParams();
    params.set(LANGUAGE_QUERY_PARAM, await this.resolveLanguage());
    return params;
  }

  /**
   * Current UI language as an ISO 639-1 code, taken from the platform's active
   * culture ({@link CultureInfoService}). Falls back to {@link DEFAULT_LANGUAGE}
   * when the active culture is unavailable or not part of
   * {@link SUPPORTED_LANGUAGES}.
   */
  private async resolveLanguage(): Promise<string> {
    const culture = await this.getCurrentCulture();
    const language = culture?.iso2LanguageCode?.toLowerCase();

    return language &&
      (SUPPORTED_LANGUAGES as readonly string[]).includes(language)
      ? language
      : DEFAULT_LANGUAGE;
  }

  private getCurrentCulture(): Promise<CultureInfo | undefined> {
    return new Promise((resolve) => {
      let subscription = this.cultureInfoService.currentCulture.subscribe(
        (cultureInfo) => {
          resolve(cultureInfo);
          subscription?.unsubscribe();
          subscription = null;
        }
      );
    });
  }
}
